/**
 * NxResumenPorPersona — agrupa un resultado fila-por-evento en un resumen por persona.
 *
 * UIA-T6 lo creo para que una respuesta que repite personas se leyera de un vistazo:
 * "Brayan (11m), Camilo (9m), Brayan (10m)..." pasaba a "Brayan 4 tardanzas / 54 min".
 *
 * UIA-T7 lo corrige, y la correccion importa mas que la version original. Aquella
 * sumaba UNA sola columna numerica y elegia la primera que encontraba: la tardanza de
 * ENTRADA. Con los datos del 27-jul al 2-ago eso decia que el peor era Brayan (65 min)
 * cuando, contando tambien el exceso de ALMUERZO, el peor es Duvan por bastante:
 *
 *      Brayan  65 entrada + 195 almuerzo = 260
 *      Duvan   62 entrada + 264 almuerzo = 326   <-- el verdadero peor
 *      Camilo  46 entrada +  57 almuerzo = 103
 *
 * O sea la vista contestaba bien una pregunta que nadie hizo y llevaba a una conclusion
 * falsa sobre una persona real. Por eso ahora suma TODAS las magnitudes y ordena por el
 * TOTAL: el reordenamiento es el arreglo, y los colores son lo que lo explican.
 *
 * Sigue siendo generico: no conoce "entrada" ni "almuerzo". Detecta la FORMA del
 * resultado, asi que cualquier consulta con varias magnitudes por persona se lee igual.
 *
 * @module presentation/shared/NxResumenPorPersona
 */

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from './texto.js';

const COLS_PERSONA = ['nombre_completo', 'colaborador', 'nombre', 'persona', 'usuario'];

// Numericas que NO son magnitudes acumulables: sumarlas daria un total sin sentido.
const NUM_NO_SUMABLES = /^(id|.*_id|ano|año|mes|dia|semana|n|total_filas|horas_efectivas)$/i;

// Paleta por concepto, estable por posicion. Misma familia que VIS-T2 para que el
// panel no hable dos idiomas de color sobre el mismo dominio.
const COLORES = ['#d29922', '#f85149', '#a371f7', '#58a6ff', '#3fb950'];

// UIA-T12 / HALLAZGO #4104 -- UN ALMUERZO DE 7 HORAS NO ES UN ALMUERZO.
//
// Mateo, 10-jul: inicio de almuerzo 14:30, fin 21:45 = 435 minutos. Brayan 10-jul: 422.
// Camilo 13-may: 306. Todos terminan entre 20:30 y 21:45, que es la hora de SALIDA del
// negocio: marcaron el fin de almuerzo AL IRSE, o nunca marcaron el regreso y el sistema
// tomo la siguiente marca.
//
// Contarlos como "exceso de almuerzo" hace dos danos a la vez: infla el total de esa
// persona y la ordena por encima de otras en la barra. La vista termina ordenando gente
// con un dato que no describe lo que dice describir.
//
// NO se filtran en silencio: un exceso de 435 minutos es informacion, dice que ese dia
// el marcaje quedo roto. Esconderlo deja el problema vivo y sin dueno. Se separan: color
// propio, segmento propio, y nombre honesto.
const UMBRAL_ALMUERZO_SIN_CERRAR = 120;
const COLOR_SIN_CERRAR = '#8957e5';
const esColumnaAlmuerzo = (c) => /almuerzo/i.test(String(c));

const esNumero = (v) => typeof v === 'number' || (typeof v === 'string' && /^-?\d+([.,]\d+)?$/.test(v.trim()));
const aNumero = (v) => typeof v === 'number' ? v : (parseFloat(String(v).replace(',', '.')) || 0);

/**
 * ¿Este resultado se lee mejor agrupado?
 *
 * Solo si hay columna de persona, al menos una magnitud sumable, y de verdad se REPITEN
 * personas. Si cada fila ya es una persona distinta, agrupar no aporta y solo mete ruido
 * en una respuesta que ya estaba bien.
 */
export function detectarAgrupable(rows) {
  if (!Array.isArray(rows) || rows.length < 3) return null;
  const cols = Object.keys(rows[0] || {});
  if (cols.length === 0) return null;

  const colPersona = COLS_PERSONA.find(c => cols.includes(c));
  if (!colPersona) return null;

  const personas = new Set(rows.map(r => r[colPersona]).filter(Boolean));
  if (personas.size === 0 || personas.size === rows.length) return null;

  // UIA-T7: TODAS las magnitudes, no la primera. Elegir una sola fue el bug.
  const colsNumericas = cols.filter(c => {
    if (c === colPersona || NUM_NO_SUMABLES.test(c)) return false;
    const valores = rows.map(r => r[c]).filter(v => v !== null && v !== undefined && v !== '');
    if (valores.length < rows.length / 2) return false;
    if (!valores.every(esNumero)) return false;
    // Una columna que es cero en todas las filas no aporta un concepto, solo leyenda.
    return valores.some(v => aNumero(v) !== 0);
  });
  if (colsNumericas.length === 0) return null;

  return { colPersona, colsNumericas, personas: personas.size, eventos: rows.length };
}

/** Etiqueta legible desde el nombre tecnico de la columna. */
function etiquetaDe(col) {
  return String(col)
    .replace(/_min$/, '')
    .replace(/_/g, ' ')
    .replace(/\bmin\b/, 'minutos')
    .trim();
}

/**
 * HTML del resumen agrupado, o '' si el resultado no se beneficia.
 * @param {Array<object>} rows
 * @param {{titulo?: string}} [opts]
 */
export function renderResumenPorPersona(rows, opts = {}) {
  const info = detectarAgrupable(rows);
  if (!info) return '';

  const { colPersona, colsNumericas } = info;

  const acc = new Map();
  for (const r of rows) {
    const p = r[colPersona];
    if (!p) continue;
    const prev = acc.get(p) || {
      persona: p, veces: 0, partes: colsNumericas.map(() => 0), total: 0,
      sinCerrar: 0, diasSinCerrar: 0
    };
    prev.veces += 1;
    colsNumericas.forEach((c, i) => {
      const n = aNumero(r[c]);
      // Un almuerzo por encima del umbral no es exceso: es un marcaje sin cerrar.
      // Sale del acumulado de su concepto y va a su propio balde, para que no ordene
      // a la persona por encima de otras con un dato que no significa lo que dice.
      if (esColumnaAlmuerzo(c) && n >= UMBRAL_ALMUERZO_SIN_CERRAR) {
        prev.sinCerrar += n;
        prev.diasSinCerrar += 1;
        // NO entra al total, y esa es la decision de fondo: el total ORDENA la barra.
        // Si los 435 minutos de un marcaje roto contaran, la persona seguiria subiendo
        // al tope por un dato que no significa lo que dice -- que es exactamente el
        // problema que el hallazgo describe. Se muestra aparte, no se suma.
      } else {
        prev.partes[i] += n;
        prev.total += n;
      }
    });
    acc.set(p, prev);
  }

  // Orden por TOTAL. Esto es el arreglo de UIA-T7: con una sola magnitud el orden
  // mentia sobre quien esta peor.
  const filas = [...acc.values()].filter(f => f.total > 0 || f.sinCerrar > 0).sort((a, b) => b.total - a.total);
  if (filas.length === 0) return '';

  const max = Math.max(...filas.map(f => f.total), 1);
  const hayVariosConceptos = colsNumericas.length > 1;

  // UIA-T9: cada barra se abre y muestra los dias de esa persona.
  //
  // Se hace con <details>, no con listeners: este componente devuelve un string que se
  // inyecta con innerHTML, asi que cualquier binding habria que re-atarlo cada vez que
  // llega una respuesta nueva. El HTML nativo ya resuelve abrir y cerrar, y ademas
  // funciona con teclado sin que yo agregue nada.
  //
  // SOLO LECTURA, deliberado: no hay boton para corregir una hora ni para justificar
  // tiempo. Eso es ESCRIBIR sobre asistencia y necesita permiso, registro de quien
  // corrigio y por que, y que el marcaje original no se pierda nunca. Es una minimision
  // propia; metida aca de contrabando saldria sin trazabilidad.
  const colsDetalle = Object.keys(rows[0] || {}).filter(c => c !== colPersona);

  const barras = filas.map(f => {
    const segmentos = f.partes.map((v, i) => {
      if (v <= 0) return '';
      return `<div title="${esc(etiquetaDe(colsNumericas[i]))}: ${v}"
        style="width:${(v / max) * 100}%;background:${COLORES[i % COLORES.length]}"></div>`;
    }).join('');
    const desglose = hayVariosConceptos
      ? f.partes.map((v, i) => v > 0 ? `${esc(etiquetaDe(colsNumericas[i]))} ${v}` : null).filter(Boolean).join(' + ')
      : '';

    // El almuerzo sin cerrar se ve, pero fuera de la barra que ordena: rayado para que
    // no se lea como una magnitud mas, y con su nombre real.
    const marcaSinCerrar = f.sinCerrar > 0
      ? `<span title="${f.diasSinCerrar} día(s) con el almuerzo sin cerrar (${f.sinCerrar} min). No se suman: casi siempre el fin de almuerzo se marcó al salir."
           style="font-size:0.875rem;color:${COLOR_SIN_CERRAR};border:1px solid ${COLOR_SIN_CERRAR};border-radius:8px;padding:1px 6px;white-space:nowrap">
           ⚑ ${f.diasSinCerrar} sin cerrar</span>`
      : '';

    // UIA-T12: cada dia en UN renglon, con iconos.
    //
    // El dato va SIEMPRE AL LADO del icono, nunca solo en el tooltip. Josse pidio
    // "posarse encima", pero en movil no hay hover: si la hora solo aparece al pasar el
    // mouse, en el telefono no se ve nunca -- y el telefono es justo donde peor se leia
    // esto. El title queda como ayuda extra en escritorio, no como unico camino.
    const dias = rows.filter(r => r[colPersona] === f.persona).map(r => {
      const val = (re) => { const k = colsDetalle.find(c => re.test(c)); return k ? r[k] : null; };
      const fecha = val(/fecha/i);
      const estado = String(val(/estado/i) || '');
      const entrada = val(/^entrada|hora_entrada|entrada_real/i);
      const salida = val(/^salida|hora_salida|salida_real/i);
      const tardanza = aNumero(val(/tardanza/i)) || 0;
      const almuerzo = aNumero(val(/almuerzo/i)) || 0;
      const sinCerrar = almuerzo >= UMBRAL_ALMUERZO_SIN_CERRAR;

      const completo = /completo/i.test(estado) && !/incompleto/i.test(estado);
      const chip = (icono, texto, color, ayuda) => texto === null || texto === '' ? '' :
        `<span title="${esc(ayuda || '')}" style="display:inline-flex;align-items:center;gap:3px;color:${color};white-space:nowrap">
           <span aria-hidden="true">${icono}</span>${esc(texto)}</span>`;

      return `
        <div style="display:flex;gap:9px;flex-wrap:wrap;align-items:center;padding:5px 0 5px 8px;border-bottom:1px solid #161b22;font-size:0.875rem;color:#8b949e">
          <span style="color:#c9d1d9;min-width:82px">${esc(fecha || '')}</span>
          ${chip(completo ? '✓' : '✗', completo ? 'completo' : (estado || 'incompleto'),
                 completo ? '#3fb950' : '#f85149', 'Estado del día')}
          ${chip('→', entrada || '—', tardanza > 5 ? '#f85149' : '#8b949e',
                 tardanza > 5 ? `Entró ${tardanza} min tarde` : 'Hora de entrada')}
          ${tardanza > 5 ? `<span style="color:#f85149">+${tardanza}m</span>` : ''}
          ${almuerzo > 0 ? chip(sinCerrar ? '⚑' : '🍽',
                 sinCerrar ? `${almuerzo}m sin cerrar` : `+${almuerzo}m almuerzo`,
                 sinCerrar ? COLOR_SIN_CERRAR : '#d29922',
                 sinCerrar
                   ? 'Probablemente marcó el fin de almuerzo al salir. No se suma al total.'
                   : 'Exceso sobre el tiempo de almuerzo') : ''}
          ${chip('←', salida || '—', '#8b949e', 'Hora de salida')}
          <!-- UIA-T12: zona de accion reservada. El layout ya esta resuelto para cuando
               exista el endpoint de correccion; hoy va vacia a proposito. No se inventa
               el endpoint ni se escribe contra attendance. -->
          <span class="nx-dia-accion" data-fecha="${esc(fecha || '')}" style="margin-left:auto"></span>
        </div>`;
    }).join('');

    return `
      <details style="margin-bottom:5px">
        <summary style="list-style:none;cursor:pointer">
          <div style="display:grid;grid-template-columns:minmax(90px,1fr) 2fr auto;gap:8px;align-items:center">
            <div style="font-size:0.875rem;color:#c9d1d9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(f.persona)}">${esc(f.persona)}</div>
            <div title="${esc(desglose)}" style="display:flex;height:16px;border-radius:3px;background:#161b22;overflow:hidden">${segmentos}</div>
            <div style="font-size:0.875rem;color:#8b949e;white-space:nowrap;display:flex;align-items:center;gap:6px">
              ${marcaSinCerrar}<span>${f.veces} × · <strong style="color:#e6edf3">${f.total}</strong></span>
            </div>
          </div>
        </summary>
        <div style="margin:4px 0 8px">${dias}</div>
      </details>`;
  }).join('');

  const leyenda = hayVariosConceptos
    ? `<div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:8px;padding-top:7px;border-top:1px solid #21262d">
        ${colsNumericas.map((c, i) => `<span style="display:inline-flex;align-items:center;gap:4px;font-size:0.875rem;color:#8b949e">
          <span style="width:9px;height:9px;border-radius:2px;background:${COLORES[i % COLORES.length]}"></span>${esc(etiquetaDe(c))}</span>`).join('')}
      </div>`
    : '';

  // El umbral que hizo chocar los numeros (UIA-T7): la grafica sumaba TODOS los
  // registros mientras el texto de al lado aplicaba un minimo, y nada lo explicaba.
  // No invento el numero del umbral -- el panel no lo conoce -- pero si digo lo que la
  // grafica hace, que es lo unico que puedo afirmar con certeza.
  // UIA-T8: el rotulo decia "27 registros" y eso se lee como 27 incidentes. No lo son:
  // son 27 dias-persona (Yazmin 7, Brayan 6, Camilo 6, Duvan 6, Luis 2) y las tardanzas
  // reales son 13. Contar dias como si fueran faltas exagera el problema por 2.
  const nota = `<div style="font-size:0.875rem;color:#6e7681;margin-top:8px">
      Suma ${info.eventos} día${info.eventos === 1 ? '' : 's'}-persona del período, sin mínimo.
      Si el texto lista menos, es porque aplica un umbral.
    </div>`;

  const titulo = opts.titulo || (hayVariosConceptos
    ? 'Por persona · total acumulado, peor primero'
    : `Agrupado por persona · ${etiquetaDe(colsNumericas[0])} acumulado`);

  return `
    <div style="border:1px solid #30363d;border-radius:6px;padding:10px 12px;margin:6px 0;background:#0d1117">
      <div style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">${esc(titulo)}</div>
      ${barras}${leyenda}${nota}
    </div>`;
}
